import {
  EmphasizedText,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Link,
  ListItem as LI,
  NumberedList as NList,
  Paragraph,
  StrongText,
  UnorderedList as UList,
} from '@entur/typography';
import { PortableText as BasePortableText } from '@portabletext/react';
import { Link as GatsbyLink } from 'gatsby';
import React from 'react';
import clientConfig from '../../client-config.js';
import { urlFor } from './ImageURL';
import slugify from 'slugify';

const PortableText = ({
  blocks,
  style,
  disableFade = false,
  disableScrollbarFix = false,
}: {
  blocks: any;
  style?: React.CSSProperties;
  disableFade?: boolean;
  disableScrollbarFix?: boolean;
}) => (
  <>
    {disableFade ? (
      <div style={style}>
        <Base blocks={blocks} />
      </div>
    ) : (
      <>
        <div style={style}>
          <Base blocks={blocks} />
        </div>
        {!disableScrollbarFix && (
          <div style={{ ...style, visibility: 'hidden' }}>
            <p>&nbsp;</p>{' '}
            {/* This is a (sub-optimal) way to prevent the double scrollbar. 
          An effort has been made to find more optimal solutions, to no avail.
        &nbsp; is an HTML-character for non-breaking space. */}
          </div>
        )}
      </>
    )}
  </>
);

const Base = ({ blocks }) => {
  return (
    <BasePortableText
      value={blocks}
      components={{
        types: {
          bilde: ContentImage,
        },
        block: BlockRenderer,
        list: {
          bullet: UnorderedList,
          number: NumberedList,
        },
        listItem: ListItem,
        marks: {
          link: ({ value, children }) => {
            const { href } = value;
            if (href === undefined) {
              return null;
            }

            if (href && href.startsWith('https://om.entur.no')) {
              return (
                <Link as={GatsbyLink} to={href.substring(19)}>
                  {children}
                </Link>
              );
            }
            return (
              <Link external href={href} target="_blank">
                {children}
              </Link>
            );
          },
          strong: ({ children }) => {
            return <StrongText>{children}</StrongText>;
          },
          em: ({ children }) => {
            return <EmphasizedText>{children}</EmphasizedText>;
          },
        },
      }}
      {...clientConfig.sanity}
    />
  );
};

const ContentImage: React.FC = ({ value }) => {
  const { asset, alt_text } = value;
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      {value?.asset && (
        <img
          src={urlFor(asset).url()}
          alt={alt_text}
          style={{
            width: '100%',
            height: '100%',
            maxHeight: '30vh',
            objectFit: 'contain',
          }}
        />
      )}
    </div>
  );
};

const UnorderedList = (props) => {
  return <UList>{props.children}</UList>;
};
const NumberedList = (props) => {
  return <NList>{props.children}</NList>;
};
const ListItem = (props) => {
  return <LI>{props.children}</LI>;
};

const BlockRenderer = (props) => {
  const { node, children } = props;
  let uniqueId;

  if (node.style.startsWith('h')) {
    const text = node.children.map((child) => child.text).join(' ');
    uniqueId = slugify(`${text}-${node._key}`, { lower: true, strict: true });
  }

  switch (node.style) {
    case 'h1':
      return (
        <Heading1 id={uniqueId} as="h2">
          {children}
        </Heading1>
      );
    case 'h2':
      return <Heading2 id={uniqueId}>{children}</Heading2>;
    case 'h3':
      return <Heading3 id={uniqueId}>{children}</Heading3>;
    case 'h4':
      return <Heading4 id={uniqueId}>{children}</Heading4>;
    case 'h5':
      return <Heading5 id={uniqueId}>{children}</Heading5>;
    default:
      return <Paragraph>{children}</Paragraph>;
  }
};

export default PortableText;
